/**
 * SalesDashboard.tsx — Power BI-style Light Corporate Sales Dashboard
 * Props-driven: receives same data as DynamicCapacityVerification from App.tsx
 * Designed for sharing with the sales team via direct link
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';
import axios from 'axios';
import { getConfig } from '../runtimeConfig';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════
interface CustMinRow {
  id: number;
  customer: string;
  warehouse: string;
  minPallets: number;
  uom: string;
  conversionFactor: number;
}

interface WhRow {
  warehouse: string;
  rackSmallBins: number; rackLargeBins: number; blockedBins: number; floorBins: number; virtualBins: number;
  rackCap: number; rackUsed: number; blockedCap: number;
  virtualUsed: number; floorPallets: number;
  manualCap: number; manualPallets: number;
  tCap: number; tUsed: number; tFree: number;
  custShortfall: number;
}

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════
const WAREHOUSE_NAMES: Record<string, string> = {
  'DPW': 'Dammam Port', 'DSI': 'Dammam 2nd', 'JDA': 'Jeddah 3A', 'JDB': 'Jeddah 3B',
  'JUB': 'Jubail', 'JUB_1': 'Jubail 1', 'JUB_2': 'Jubail 2', 'RUH': 'Riyadh', 'RUH_M': 'Riyadh M'
};
const whName = (code: string) => WAREHOUSE_NAMES[code] || code;
const THRESHOLD = 10;
const fmt = (n: number) => n >= 1000 ? n.toLocaleString('en-US') : String(n);

// Color palette — Power BI corporate
const C = {
  primary: '#4668c5',
  success: '#2ecc71',
  warning: '#f39c12',
  danger: '#e74c3c',
  purple: '#8b5cf6',
  teal: '#0ea5e9',
  orange: '#e67e22',
  bg: '#f0f2f5',
  card: '#ffffff',
  text: '#1e293b',
  textMuted: '#64748b',
  textLight: '#94a3b8',
  border: '#e2e8f0',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  shadowHover: '0 10px 25px rgba(0,0,0,0.08), 0 4px 10px rgba(0,0,0,0.04)',
};

// ═══════════════════════════════════════════════════════
// ANIMATED NUMBER COUNTER
// ═══════════════════════════════════════════════════════
const AnimatedNumber: React.FC<{ value: number; duration?: number; suffix?: string; prefix?: string }> = ({ value, duration = 1200, suffix = '', prefix = '' }) => {
  const [display, setDisplay] = useState(0);
  const prevRef = useRef(0);
  const frameRef = useRef(0);

  useEffect(() => {
    const start = prevRef.current;
    const end = value;
    const startTime = performance.now();
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(start + (end - start) * eased);
      setDisplay(current);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      } else {
        prevRef.current = end;
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [value, duration]);

  return <>{prefix}{display >= 1000 ? display.toLocaleString('en-US') : display}{suffix}</>;
};

// ═══════════════════════════════════════════════════════
// PROPS — same data sources as DynamicCapacityVerification
// ═══════════════════════════════════════════════════════
interface SalesDashboardProps {
  rawWarehouseData: any[];
  rawVirtualData: any[];
  customerStorageData: any[];
  manualEntries: any[];
  areaCustomerBlockedData: any[];
  loading: boolean;
  onRefresh: () => void;
  lastRefreshTime?: string;
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
const SalesDashboard: React.FC<SalesDashboardProps> = ({
  rawWarehouseData: propRawData,
  rawVirtualData: propVirtualData,
  customerStorageData: propCustData,
  manualEntries: propManualEntries,
  areaCustomerBlockedData: propBlockedData,
  loading: propLoading,
  onRefresh,
  lastRefreshTime,
}) => {
  const API = getConfig().REACT_APP_API;

  // ── UI state ──
  const [selectedWarehouse, setSelectedWarehouse] = useState('All');
  const [drillOpen, setDrillOpen] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [custMinRows, setCustMinRows] = useState<CustMinRow[]>([]);
  const [contractsLoaded, setContractsLoaded] = useState(false);
  const [expandedChamber, setExpandedChamber] = useState<string | null>(null);
  const [selectedKpi, setSelectedKpi] = useState<null | 'capacity' | 'used' | 'sellable' | 'locations' | 'clients'>(null);

  // Use prop data directly
  const rawWarehouseData = propRawData || [];
  const rawVirtualData = propVirtualData || [];
  const customerStorageData = propCustData || [];
  const manualEntries = propManualEntries || [];

  // Conversion factors (same as ExecutiveDashboard)
  const COMMITMENT_FACTORS: Record<string, number> = {
    'PP': 1, 'SQM': 0.8333, 'SQFT': 0.07742, 'BINS': 1, 'MT': 0.714, 'KT': 714.29, 'CBM': 0.5,
  };

  // Fetch customer contracts (only thing not provided by App.tsx)
  useEffect(() => {
    if (contractsLoaded) return;
    axios.get(`${API}/customer-contracts?active=true`).then(res => {
      const json = res.data;
      const contracts = json?.data || json || [];
      setCustMinRows(contracts.map((c: any) => {
        const cType = (c.storage_type || 'PP').toUpperCase();
        const factor = COMMITMENT_FACTORS[cType] || 1;
        const minVal = c.minimum_value || 0;
        return {
          id: c.id, customer: c.customer_name || '', warehouse: c.facility || 'All',
          minPallets: Math.ceil(minVal * factor), uom: cType, conversionFactor: factor,
        };
      }));
      setContractsLoaded(true);
    }).catch(() => setContractsLoaded(true));
  }, [API, contractsLoaded]);

  useEffect(() => {
    if (rawWarehouseData.length > 0) setTimeout(() => setFadeIn(true), 100);
  }, [rawWarehouseData.length]);

  // ═══════════════════════════════════════════════════════
  // DATA COMPUTATION — matches DynamicCapacityVerification exactly
  // ═══════════════════════════════════════════════════════

  // Cap: seg 1-2 → as-is, seg 3-9 + level=10 → 2, seg 3-9 + level≠10 → 1, seg ≥10 → as-is.
  // Used: always raw seg - free.
  const calcCap = (seg: number, lvl: number) => {
    if (seg <= 0) return 0;
    if (seg <= 2) return seg;
    if (seg < THRESHOLD) return lvl === 10 ? 2 : 1;
    return seg;
  };

  // ── Manual entries: latest week per warehouse::client (same as DCV) ──
  const manualByWh = useMemo(() => {
    const m: Record<string, { pallets: number; capacity: number }> = {};
    // manualEntries from App.tsx = customer data with STORAGE_TYPE='MANUAL'
    // These already have UPPERCASE fields from the merged endpoint
    // Group by warehouse, SUM pallets, MAX capacity per area
    const capByArea: Record<string, number> = {};
    (manualEntries || []).forEach((e: any) => {
      const wh = (e.WAREHOUSE || e.warehouse || '').trim();
      if (!wh) return;
      if (!m[wh]) m[wh] = { pallets: 0, capacity: 0 };
      m[wh].pallets += (e.USED_PALLETS || e.closing_balance || 0);
      const area = e.STORAGE_LOCATION || e.warehouse_area || 'MANUAL';
      const aKey = `${wh}__${area}`;
      capByArea[aKey] = Math.max(capByArea[aKey] || 0, e.TOTAL_CAPACITY || e.area_capacity || e.capacity || 0);
    });
    // Apply deduplicated capacity
    Object.entries(capByArea).forEach(([key, cap]) => {
      const wh = key.split('__')[0];
      if (m[wh]) m[wh].capacity += cap;
    });
    return m;
  }, [manualEntries]);

  // ── Customer IBM pallets per warehouse (for floor calculation) ──
  // MUST only count STORAGE_TYPE='IBM' — matches DCV's customerByWh
  const ibmByWh = useMemo(() => {
    const m: Record<string, { pallets: number; clients: Set<string> }> = {};
    (customerStorageData || []).forEach((item: any) => {
      const wh = (item.WAREHOUSE || '').trim();
      if (!wh) return;
      if (!m[wh]) m[wh] = { pallets: 0, clients: new Set() };
      // Only count IBM storage type for floor pallet calculation
      if (item.STORAGE_TYPE === 'IBM') {
        m[wh].pallets += (item.USED_PALLETS || 0);
      }
      // Count ALL customers (including MANUAL) for client count
      const cust = (item.CUSTOMER || '').trim();
      if (cust && item.STORAGE_TYPE !== 'MANUAL') m[wh].clients.add(cust);
    });
    // Also add manual clients
    (manualEntries || []).forEach((item: any) => {
      const wh = (item.WAREHOUSE || item.warehouse || '').trim();
      if (!wh) return;
      if (!m[wh]) m[wh] = { pallets: 0, clients: new Set() };
      const cust = (item.CUSTOMER || item.client_name || '').trim();
      if (cust) m[wh].clients.add(cust);
    });
    return m;
  }, [customerStorageData, manualEntries]);

  // ── Customer actual pallets (for commitment calc) ──
  const custActualPallets = useMemo(() => {
    const map: Record<string, number> = {};
    (customerStorageData || []).forEach((item: any) => {
      const cust = (item.CUSTOMER || '').trim();
      const wh = (item.WAREHOUSE || '').trim();
      if (!cust) return;
      map[`${cust}__${wh}`] = (map[`${cust}__${wh}`] || 0) + (item.USED_PALLETS || 0);
      map[cust] = (map[cust] || 0) + (item.USED_PALLETS || 0);
    });
    return map;
  }, [customerStorageData]);

  // ═══════════════════════════════════════════════════════
  // DYNAMIC CAPACITY STATS — unified single source of truth
  // Matches DynamicCapacityVerification computation exactly
  // ═══════════════════════════════════════════════════════
  const stats = useMemo(() => {
    const isAll = selectedWarehouse === 'All';
    const whFilter = (wh: string) => isAll || wh === selectedWarehouse;

    const whMap: Record<string, WhRow> = {};
    const ensureWh = (wh: string) => {
      if (!whMap[wh]) whMap[wh] = {
        warehouse: wh, rackSmallBins: 0, rackLargeBins: 0, blockedBins: 0, floorBins: 0, virtualBins: 0,
        rackCap: 0, rackUsed: 0, blockedCap: 0, virtualUsed: 0, floorPallets: 0,
        manualCap: 0, manualPallets: 0, tCap: 0, tUsed: 0, tFree: 0, custShortfall: 0,
      };
    };

    // 1. Process rawWarehouseData (Rack Small, Rack Large, Blocked, Floor bins)
    (rawWarehouseData || []).forEach((item: any) => {
      if (item.SOURCE === 'MANUAL') return;
      const wh = (item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      ensureWh(wh);
      const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
      const free = item.FREE_SEGMENTS || 0;
      const lvl = item.NUMBER_OF_LEVELS || 0;
      const blocked = String(item.LPFSTA || '').trim() === '2' || item.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
      if (blocked) {
        whMap[wh].blockedBins += 1;
        whMap[wh].blockedCap += calcCap(seg, lvl);
      } else if (seg === 0) {
        whMap[wh].floorBins += 1;
      } else if (seg >= 99) {
        // skip virtual from raw — use rawVirtualData instead
      } else if (seg > THRESHOLD) {
        whMap[wh].rackLargeBins += 1;
        whMap[wh].rackCap += seg;
        whMap[wh].rackUsed += Math.max(0, seg - free);
      } else {
        whMap[wh].rackSmallBins += 1;
        whMap[wh].rackCap += calcCap(seg, lvl);
        whMap[wh].rackUsed += Math.max(0, seg - free);
      }
    });

    // 2. Virtual — from rawVirtualData (individual bins, same calc as DCV)
    (rawVirtualData || []).forEach((item: any) => {
      const wh = (item.WAREHOUSE || '').trim();
      if (!whFilter(wh)) return;
      ensureWh(wh);
      const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
      const free = item.FREE_SEGMENTS || 0;
      const lvl = item.NUMBER_OF_LEVELS || 0;
      whMap[wh].virtualBins += 1;
      whMap[wh].virtualUsed += Math.max(0, seg - free);
    });

    // 3. Manual — from manualByWh (processed from manualEntries prop)
    Object.entries(manualByWh).forEach(([wh, man]) => {
      if (!whFilter(wh)) return;
      ensureWh(wh);
      whMap[wh].manualPallets += man.pallets;
      whMap[wh].manualCap += man.capacity;
    });

    // 4. Ensure warehouses from customer storage data are in whMap
    Object.entries(ibmByWh).forEach(([wh]) => {
      if (!whFilter(wh)) return;
      ensureWh(wh);
    });

    // 5. Compute totals:
    //    tCap = rackCap + blockedCap + manualCap
    //    tUsed = rackUsed + blockedCap + virtualUsed + manualPallets + floorPallets
    //    tFree (Sellable) = max(0, tCap - tUsed - custShortfall)
    //    => Capacity = Used + Commitment + Sellable
    Object.values(whMap).forEach(row => {
      const ibmPallets = (ibmByWh[row.warehouse]?.pallets) || 0;
      row.floorPallets = Math.max(0, ibmPallets - row.rackUsed - row.blockedCap);
      row.tCap = row.rackCap + row.blockedCap + row.manualCap;
      row.tUsed = row.rackUsed + row.blockedCap + row.virtualUsed + row.manualPallets + row.floorPallets;
      // tFree computed after commitment shortfall is calculated (step 6)
    });

    // 6. Customer commitment shortfall
    let totalCustShortfall = 0;
    const allWhKeys = Object.keys(whMap);
    const allWhSet = new Set<string>();
    (rawWarehouseData || []).forEach((i: any) => { if (i.WAREHOUSE) allWhSet.add(i.WAREHOUSE); });
    const totalWhCount = Math.max(allWhSet.size, 1);

    (custMinRows || []).forEach(cmr => {
      const customers = cmr.customer.split(',').map(c => c.trim()).filter(Boolean);
      const cmrWhs = cmr.warehouse === 'All' ? ['All'] : cmr.warehouse.split(',').map(w => w.trim()).filter(Boolean);
      if (!isAll && !cmrWhs.includes('All') && !cmrWhs.includes(selectedWarehouse)) return;
      let actual = 0;
      customers.forEach(cust => {
        if (cmrWhs.includes('All')) {
          actual += isAll ? (custActualPallets[cust] || 0) : (custActualPallets[`${cust}__${selectedWarehouse}`] || 0);
        } else {
          cmrWhs.forEach(wh => {
            if (isAll || wh === selectedWarehouse) actual += (custActualPallets[`${cust}__${wh}`] || 0);
          });
        }
      });
      let effectiveMin = cmr.minPallets;
      if (!isAll && cmrWhs.includes('All')) effectiveMin = Math.ceil(cmr.minPallets / totalWhCount);
      const shortfall = Math.max(0, effectiveMin - actual);
      totalCustShortfall += shortfall;
      if (shortfall > 0) {
        if (isAll && cmrWhs.includes('All')) {
          const per = Math.ceil(shortfall / Math.max(allWhKeys.length, 1));
          let rem = shortfall;
          allWhKeys.forEach(wh => { const s = Math.min(per, rem); if (whMap[wh]) whMap[wh].custShortfall += s; rem -= s; });
        } else if (!isAll) {
          if (whMap[selectedWarehouse]) whMap[selectedWarehouse].custShortfall += shortfall;
        } else {
          const per = Math.ceil(shortfall / cmrWhs.length);
          let rem = shortfall;
          cmrWhs.forEach(wh => { const s = Math.min(per, rem); if (whMap[wh]) whMap[wh].custShortfall += s; rem -= s; });
        }
      }
    });

    // Now compute tFree (sellable) AFTER commitment shortfall is distributed
    // Sellable = max(0, tCap - tUsed - custShortfall)
    // This ensures: Capacity = Used + Commitment + Sellable
    Object.values(whMap).forEach(row => {
      row.tFree = Math.max(0, row.tCap - row.tUsed - row.custShortfall);
    });

    const rows = Object.values(whMap).sort((a, b) => a.warehouse.localeCompare(b.warehouse));
    const grandCap = rows.reduce((s, r) => s + r.tCap, 0);
    const grandUsed = rows.reduce((s, r) => s + r.tUsed, 0);
    const grandFree = Math.max(0, grandCap - grandUsed - totalCustShortfall);
    const totalLocs = rows.reduce((s, r) => s + r.rackSmallBins + r.rackLargeBins + r.blockedBins, 0);
    const totalClients = new Set(Object.values(ibmByWh).flatMap(entry => Array.from(entry.clients))).size;

    return {
      rackUsed: rows.reduce((s, r) => s + r.rackUsed, 0),
      blockedCap: rows.reduce((s, r) => s + r.blockedCap, 0),
      virtualUsed: rows.reduce((s, r) => s + r.virtualUsed, 0),
      floorPallets: rows.reduce((s, r) => s + r.floorPallets, 0),
      manualPallets: rows.reduce((s, r) => s + r.manualPallets, 0),
      manualCap: rows.reduce((s, r) => s + r.manualCap, 0),
      custShortfall: totalCustShortfall,
      totalCapacity: grandCap,
      totalUsed: grandUsed,
      totalFree: grandFree,
      totalLocations: totalLocs,
      totalClients,
      warehouseRows: rows,
      utilPct: grandCap > 0 ? Math.min(100, Math.round((grandUsed / grandCap) * 1000) / 10) : 0,
    };
  }, [rawWarehouseData, rawVirtualData, customerStorageData, manualByWh, ibmByWh, selectedWarehouse, custMinRows, custActualPallets]);

  // ── Warehouse list for filter — includes ALL data sources (matches DCV) ──
  const warehouseList = useMemo(() => {
    const set = new Set<string>();
    (rawWarehouseData || []).forEach((i: any) => { const w = (i.WAREHOUSE || '').trim(); if (w) set.add(w); });
    (rawVirtualData || []).forEach((i: any) => { const w = (i.WAREHOUSE || '').trim(); if (w) set.add(w); });
    (manualEntries || []).forEach((i: any) => { const w = (i.WAREHOUSE || i.warehouse || '').trim(); if (w) set.add(w); });
    (customerStorageData || []).forEach((i: any) => { const w = (i.WAREHOUSE || '').trim(); if (w) set.add(w); });
    return Array.from(set).sort();
  }, [rawWarehouseData, rawVirtualData, manualEntries, customerStorageData]);

  // ── Drill-down data (rack-only breakdown by chamber & pallet type) ──
  const drillData = useMemo(() => {
    let bins = (rawWarehouseData || []).filter((item: any) => {
      if (item.SOURCE === 'MANUAL') return false;
      const seg = item.TOTAL_NO_OF_SEGMENTS || 0;
      return seg > 0 && seg < 99;
    });
    if (selectedWarehouse !== 'All') bins = bins.filter((b: any) => (b.WAREHOUSE || '').trim() === selectedWarehouse);

    const enriched = bins.map((b: any) => {
      const seg = b.TOTAL_NO_OF_SEGMENTS || 0, free = b.FREE_SEGMENTS || 0, lvl = b.NUMBER_OF_LEVELS || 0;
      const blocked = String(b.LPFSTA || '').trim() === '2' || b.OCCUPANCY_STATUS === 'TEMP_BLOCKED';
      const cap = calcCap(seg, lvl);
      const used = blocked ? cap : Math.max(0, seg - free);
      return { wh: (b.WAREHOUSE || '').trim(), chamber: (b.STORAGE_AREA || '').trim(), shelfSize: String(b.SHELF_SIZE || '').trim(), level: lvl, seg, free, cap, used, blocked };
    });

    // Chamber breakdown with warehouse name & level detail
    const chmMap: Record<string, { chamber: string; wh: string; cap: number; used: number; blocked: number; blockedCap: number; bins: number }> = {};
    enriched.forEach(b => {
      if (!chmMap[b.chamber]) chmMap[b.chamber] = { chamber: b.chamber, wh: b.wh, cap: 0, used: 0, blocked: 0, blockedCap: 0, bins: 0 };
      chmMap[b.chamber].cap += b.cap; chmMap[b.chamber].used += b.used; chmMap[b.chamber].bins += 1;
      if (b.blocked) { chmMap[b.chamber].blocked++; chmMap[b.chamber].blockedCap += b.cap; }
    });
    const chambers = Object.values(chmMap).map(c => ({
      ...c, sellable: Math.max(0, c.cap - c.blockedCap - c.used),
      utilPct: c.cap > 0 ? Math.round((c.used / c.cap) * 1000) / 10 : 0,
    })).sort((a, b) => b.cap - a.cap);

    // Level-by-level breakdown per chamber (for drill-down)
    const chamberLevels: Record<string, { level: number; bins: number; cap: number; used: number; free: number; blocked: number; blockedCap: number; shelfSizes: Record<string, number> }[]> = {};
    enriched.forEach(b => {
      if (!chamberLevels[b.chamber]) chamberLevels[b.chamber] = [];
      let lvlEntry = chamberLevels[b.chamber].find(l => l.level === b.level);
      if (!lvlEntry) {
        lvlEntry = { level: b.level, bins: 0, cap: 0, used: 0, free: 0, blocked: 0, blockedCap: 0, shelfSizes: {} };
        chamberLevels[b.chamber].push(lvlEntry);
      }
      lvlEntry.bins += 1; lvlEntry.cap += b.cap; lvlEntry.used += b.used; lvlEntry.free += Math.max(0, b.cap - b.used);
      if (b.blocked) { lvlEntry.blocked++; lvlEntry.blockedCap += b.cap; }
      const sz = b.shelfSize || 'N/A';
      lvlEntry.shelfSizes[sz] = (lvlEntry.shelfSizes[sz] || 0) + 1;
    });
    // Sort levels ascending within each chamber
    Object.values(chamberLevels).forEach(arr => arr.sort((a, b) => a.level - b.level));

    // Pallet type breakdown
    const ptMap: Record<string, { type: string; cap: number; used: number; blocked: number; blockedCap: number }> = {};
    enriched.forEach(b => {
      const pt = b.shelfSize || 'Unknown';
      if (!ptMap[pt]) ptMap[pt] = { type: pt, cap: 0, used: 0, blocked: 0, blockedCap: 0 };
      ptMap[pt].cap += b.cap; ptMap[pt].used += b.used;
      if (b.blocked) { ptMap[pt].blocked++; ptMap[pt].blockedCap += b.cap; }
    });
    const palletTypes = Object.values(ptMap).map(p => ({
      ...p, sellable: Math.max(0, p.cap - p.blockedCap - p.used),
      utilPct: p.cap > 0 ? Math.round((p.used / p.cap) * 1000) / 10 : 0,
    })).sort((a, b) => b.cap - a.cap);

    // Client breakdown
    let custData = (customerStorageData || []).filter((c: any) => {
      if (c.STORAGE_TYPE === 'VIRTUAL' || c.STORAGE_TYPE === 'FLOOR' || c.STORAGE_TYPE === 'BULK') return false;
      if (selectedWarehouse !== 'All') return (c.WAREHOUSE || '').trim() === selectedWarehouse;
      return true;
    });
    const clientMap: Record<string, { client: string; used: number; cap: number }> = {};
    custData.forEach((c: any) => {
      const k = c.CUSTOMER || 'Unknown';
      if (!clientMap[k]) clientMap[k] = { client: k, used: 0, cap: 0 };
      clientMap[k].used += (c.USED_PALLETS || 0);
      clientMap[k].cap += (c.TOTAL_CAPACITY || 0);
    });
    const clients = Object.values(clientMap).sort((a, b) => b.used - a.used);

    return { chambers, chamberLevels, palletTypes, clients };
  }, [rawWarehouseData, customerStorageData, selectedWarehouse]);

  // ═══════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════

  // Loading state
  const loading = propLoading;
  if (loading && rawWarehouseData.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>Loading Dashboard...</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>Fetching warehouse data</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    );
  }

  const d = stats;
  const utilPct = d.utilPct;
  const sellable = d.totalFree;

  // Gauge SVG math
  const gW = 220, gH = 125, gCx = gW / 2, gCy = 105, gR = 80;
  const gaugeAngle = Math.PI - (Math.min(utilPct, 100) / 100) * Math.PI;
  const nX = gCx + gR * 0.78 * Math.cos(gaugeAngle);
  const nY = gCy - gR * 0.78 * Math.sin(gaugeAngle);

  // Donut data — show all usage components + sellable
  const donutData = [
    { name: 'Rack Used', value: d.rackUsed, fill: C.primary },
    { name: 'Blocked', value: d.blockedCap, fill: C.danger },
    { name: 'Virtual', value: d.virtualUsed, fill: C.teal },
    { name: 'Floor', value: d.floorPallets, fill: C.orange },
    { name: 'Manual', value: d.manualPallets, fill: C.purple },
    { name: 'Sellable', value: sellable, fill: C.success },
    ...(d.custShortfall > 0 ? [{ name: 'Commitment', value: d.custShortfall, fill: C.warning }] : []),
  ].filter(x => x.value > 0);

  // Warehouse bars — use tUsed / tCap (matches DCV utilPct)
  const whBars = d.warehouseRows.slice(0, 10).map(w => {
    return {
      name: whName(w.warehouse),
      code: w.warehouse,
      used: w.tUsed,
      cap: w.tCap,
      pct: w.tCap > 0 ? Math.min(100, Math.round((w.tUsed / w.tCap) * 1000) / 10) : 0,
    };
  }).sort((a, b) => b.pct - a.pct);

  // Source breakdown for drill-down
  const sources = [
    { label: 'Rack', value: d.rackUsed, color: C.primary },
    { label: 'Blocked', value: d.blockedCap, color: C.danger },
    { label: 'Virtual', value: d.virtualUsed, color: C.teal },
    { label: 'Floor', value: d.floorPallets, color: C.success },
    { label: 'Manual', value: d.manualPallets, color: C.purple },
    { label: 'Commitment', value: d.custShortfall, color: C.warning },
  ].filter(s => s.value > 0);

  // ── Styles ──
  const cardStyle: React.CSSProperties = {
    background: C.card, borderRadius: 12, padding: '20px 24px',
    boxShadow: C.shadow, border: `1px solid ${C.border}`,
    transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden',
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      fontFamily: "'Segoe UI', -apple-system, system-ui, sans-serif",
      opacity: fadeIn ? 1 : 0, transition: 'opacity 0.6s ease',
    }}>
      {/* CSS animations */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.02) } }
        @keyframes shimmer { 0% { background-position: -200% 0 } 100% { background-position: 200% 0 } }
        .kpi-card:hover { transform: translateY(-4px); box-shadow: ${C.shadowHover} !important; }
        .wh-bar:hover { background: #f8fafc !important; }
        .drill-btn:hover { background: ${C.primary} !important; color: #fff !important; }
        .wh-filter:hover { border-color: ${C.primary} !important; }
        .wh-filter:focus { border-color: ${C.primary} !important; box-shadow: 0 0 0 3px rgba(70,104,197,0.15) !important; outline: none; }
        .slide-panel { animation: slideUp 0.4s ease forwards; }
      `}</style>

      {/* ═══ HEADER BAR ═══ */}
      <div style={{
        background: '#fff', borderBottom: `1px solid ${C.border}`,
        padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10,
            background: `linear-gradient(135deg, ${C.primary}, #5b7fd6)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(70,104,197,0.3)',
          }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>H</span>
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.text, letterSpacing: '-0.3px' }}>
              Hala Warehousing
            </div>
            <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 500 }}>Sales Operations Dashboard</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Warehouse filter */}
          <select
            className="wh-filter"
            value={selectedWarehouse}
            onChange={e => setSelectedWarehouse(e.target.value)}
            style={{
              padding: '8px 32px 8px 14px', borderRadius: 8,
              border: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600,
              color: C.text, background: '#fff', cursor: 'pointer',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M3 5l3 3 3-3' fill='none' stroke='%2364748b' stroke-width='1.5'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
              transition: 'all 0.2s ease',
            }}
          >
            <option value="All">All Warehouses</option>
            {warehouseList.map(w => <option key={w} value={w}>{whName(w)} ({w})</option>)}
          </select>
          {lastRefreshTime && (
            <span style={{ fontSize: 11, color: C.textLight }}>
              Updated {lastRefreshTime}
            </span>
          )}
          <button
            onClick={onRefresh}
            style={{
              padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.primary}`,
              background: 'transparent', color: C.primary, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.primary; }}
          >
            {propLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>
      {/* Error display removed — data comes from parent App.tsx */}

      {/* ═══ MAIN CONTENT ═══ */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 32px' }}>

        {/* ── KPI CARDS ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 28 }}>
          {([
            { key: 'capacity' as const, label: 'Total Capacity', value: d.totalCapacity, sub: 'Rack + Manual positions', icon: '📦', color: C.primary, accent: '#eef2ff' },
            { key: 'used' as const, label: 'Total Used', value: d.totalUsed, sub: 'All sources combined', icon: '📊', color: C.orange, accent: '#fff7ed' },
            { key: 'sellable' as const, label: 'Sellable Space', value: sellable, sub: 'Available for sale', icon: '✅', color: C.success, accent: '#f0fdf4' },
            { key: 'locations' as const, label: 'Rack Locations', value: d.totalLocations, sub: 'Physical bin positions', icon: '🏗️', color: C.teal, accent: '#f0f9ff' },
            { key: 'clients' as const, label: 'Active Clients', value: d.totalClients, sub: 'Customers with storage', icon: '👥', color: C.purple, accent: '#faf5ff' },
          ]).map((kpi, i) => (
            <div
              key={i}
              className="kpi-card"
              onClick={() => setSelectedKpi(selectedKpi === kpi.key ? null : kpi.key)}
              style={{
                ...cardStyle,
                animation: `slideUp 0.5s ease ${i * 0.08}s both`,
                cursor: 'pointer',
                outline: selectedKpi === kpi.key ? `2px solid ${kpi.color}` : 'none',
                boxShadow: selectedKpi === kpi.key ? `0 8px 24px ${kpi.color}33` : (cardStyle as any).boxShadow,
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {/* Accent stripe */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${kpi.color}, ${kpi.color}88)` }} />
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {kpi.label}
                </div>
                <div style={{
                  width: 32, height: 32, borderRadius: 8, background: kpi.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                }}>
                  {kpi.icon}
                </div>
              </div>
              <div style={{ fontSize: 32, fontWeight: 800, color: C.text, lineHeight: 1, letterSpacing: '-1px', marginBottom: 4 }}>
                <AnimatedNumber value={kpi.value} />
              </div>
              <div style={{ fontSize: 11, color: C.textLight }}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* ── KPI DRILL: Per-warehouse breakdown ── */}
        {selectedKpi && (() => {
          const cfg: Record<string, { title: string; color: string; accent: string; getVal: (w: any) => number; fmtVal: (v: number) => string }> = {
            capacity: { title: 'Total Capacity by Warehouse', color: C.primary, accent: '#eef2ff', getVal: w => w.tCap, fmtVal: fmt },
            used:     { title: 'Total Used by Warehouse',     color: C.orange,  accent: '#fff7ed', getVal: w => w.tUsed, fmtVal: fmt },
            sellable: { title: 'Sellable Space by Warehouse', color: C.success, accent: '#f0fdf4', getVal: w => Math.max(0, w.tCap - w.tUsed - (w.custShortfall || 0)), fmtVal: fmt },
            locations:{ title: 'Rack Locations by Warehouse', color: C.teal,    accent: '#f0f9ff', getVal: w => w.locations || w.rackCap || 0, fmtVal: fmt },
            clients:  { title: 'Active Clients by Warehouse', color: C.purple,  accent: '#faf5ff', getVal: w => (w.clients instanceof Set ? w.clients.size : (w.clientCount || 0)), fmtVal: fmt },
          };
          const c = cfg[selectedKpi];
          const rows = [...d.warehouseRows].map(w => ({ ...w, _v: c.getVal(w) })).sort((a, b) => b._v - a._v);
          const max = Math.max(1, ...rows.map(r => r._v));
          const total = rows.reduce((s, r) => s + r._v, 0);
          return (
            <div style={{ ...cardStyle, marginBottom: 28, animation: 'slideUp 0.4s ease', borderTop: `3px solid ${c.color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{c.title}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Total: <b style={{ color: c.color }}>{c.fmtVal(total)}</b> across {rows.length} warehouses · click card again to close</div>
                </div>
                <button onClick={() => setSelectedKpi(null)} style={{ background: c.accent, border: `1px solid ${c.color}33`, color: c.color, padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>✕ Close</button>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {rows.map((w, i) => {
                  const pct = max > 0 ? (w._v / max) * 100 : 0;
                  const sharePct = total > 0 ? ((w._v / total) * 100).toFixed(1) : '0';
                  return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '160px 1fr 100px 60px', gap: 12, alignItems: 'center', padding: '8px 12px', background: i % 2 === 0 ? '#fafbfc' : '#fff', borderRadius: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.warehouse}</div>
                      <div style={{ position: 'relative', height: 22, background: '#eef2f5', borderRadius: 4, overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: `linear-gradient(90deg, ${c.color}, ${c.color}cc)`, transition: 'width 0.6s ease', borderRadius: 4 }} />
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: c.color, textAlign: 'right' }}>{c.fmtVal(w._v)}</div>
                      <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'right' }}>{sharePct}%</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── MAIN VISUALIZATION ROW ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr', gap: 16, marginBottom: 28 }}>

          {/* GAUGE — Utilization Rate */}
          <div style={{ ...cardStyle, animation: 'slideUp 0.6s ease 0.4s both' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Utilization Rate</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>Overall capacity usage</div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <svg width={gW} height={gH} viewBox={`0 0 ${gW} ${gH}`}>
                <defs>
                  <linearGradient id="sdGaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={C.success} />
                    <stop offset="60%" stopColor={C.warning} />
                    <stop offset="100%" stopColor={C.danger} />
                  </linearGradient>
                  <filter id="sdGlow">
                    <feGaussianBlur stdDeviation="2" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                {/* Background arc */}
                <path d={`M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 0 1 ${gCx + gR} ${gCy}`}
                  fill="none" stroke="#e8ecf1" strokeWidth="16" strokeLinecap="round" />
                {/* Colored arc */}
                <path d={`M ${gCx - gR} ${gCy} A ${gR} ${gR} 0 0 1 ${gCx + gR} ${gCy}`}
                  fill="none" stroke="url(#sdGaugeGrad)" strokeWidth="16" strokeLinecap="round"
                  pathLength="100" strokeDasharray={`${Math.min(utilPct, 100)} 100`}
                  style={{ transition: 'stroke-dasharray 1.2s ease' }} />
                {/* Needle */}
                <line x1={gCx} y1={gCy} x2={nX} y2={nY}
                  stroke={C.text} strokeWidth="2.5" strokeLinecap="round" filter="url(#sdGlow)"
                  style={{ transition: 'all 1.2s ease' }} />
                <circle cx={gCx} cy={gCy} r="5" fill={C.text} />
                {/* Labels */}
                <text x={gCx - gR - 2} y={gCy + 16} fontSize="10" fill={C.textLight} textAnchor="middle">0%</text>
                <text x={gCx + gR + 2} y={gCy + 16} fontSize="10" fill={C.textLight} textAnchor="middle">100%</text>
                <text x={gCx} y={gCy + 24} textAnchor="middle" fontSize="28" fontWeight="800" fill={C.text}>
                  {utilPct}%
                </text>
              </svg>
            </div>
          </div>

          {/* DONUT — Usage Distribution */}
          <div style={{ ...cardStyle, animation: 'slideUp 0.6s ease 0.5s both' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>Space Distribution</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>How capacity is allocated</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height={170}>
                  <PieChart>
                    <Pie
                      data={donutData} cx="50%" cy="50%"
                      innerRadius={45} outerRadius={72}
                      paddingAngle={3} dataKey="value"
                      animationBegin={300} animationDuration={1200}
                      animationEasing="ease-out"
                    >
                      {donutData.map((d, i) => <Cell key={i} fill={d.fill} stroke="#fff" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip
                      formatter={(v: any) => fmt(Number(v))}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${C.border}`, boxShadow: C.shadow }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 140 }}>
                {donutData.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: d.fill, flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 11, color: C.textMuted, lineHeight: 1.2 }}>{d.name}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{fmt(d.value)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* WAREHOUSE BARS — Utilization by Warehouse */}
          <div style={{ ...cardStyle, animation: 'slideUp 0.6s ease 0.6s both', padding: '20px 20px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 4 }}>By Warehouse</div>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>Utilization ranking</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {whBars.slice(0, 6).map((w, i) => {
                const barColor = w.pct > 90 ? C.danger : w.pct > 75 ? C.warning : C.success;
                return (
                  <div key={i} className="wh-bar" style={{ padding: '4px 0', borderRadius: 4, transition: 'background 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{w.name}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: barColor }}>{w.pct}%</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', width: `${Math.min(100, w.pct)}%`, background: barColor,
                        borderRadius: 3, transition: 'width 1.2s ease',
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── VIEW DETAILS BUTTON ── */}
        <div style={{ textAlign: 'center', marginBottom: drillOpen ? 0 : 28 }}>
          <button
            className="drill-btn"
            onClick={() => setDrillOpen(!drillOpen)}
            style={{
              padding: '12px 36px', borderRadius: 10,
              border: `2px solid ${C.primary}`, background: drillOpen ? C.primary : 'transparent',
              color: drillOpen ? '#fff' : C.primary, fontSize: 14, fontWeight: 700,
              cursor: 'pointer', transition: 'all 0.3s ease', letterSpacing: '0.3px',
            }}
          >
            {drillOpen ? 'Hide Details ▲' : 'View Detailed Breakdown ▼'}
          </button>
        </div>

        {/* ═══ DRILL-DOWN PANEL ═══ */}
        {drillOpen && (
          <div className="slide-panel" style={{ marginTop: 20, marginBottom: 28 }}>

            {/* Source Breakdown */}
            <div style={{ ...cardStyle, marginBottom: 16, padding: 24 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 16 }}>Used Pallets by Source</div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(sources.length, 6)}, 1fr)`, gap: 12 }}>
                {sources.map((s, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: '16px 8px', borderRadius: 10, background: `${s.color}08`, border: `1px solid ${s.color}20` }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{fmt(s.value)}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, marginTop: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>
                      {d.totalUsed > 0 ? Math.round((s.value / d.totalUsed) * 100) : 0}% of total
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chamber Breakdown — Full Width, Clickable with Level Drill-down */}
            <div style={{ ...cardStyle, padding: 20, marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Chamber / Area Breakdown</div>
                  <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Click any chamber to see level-by-level detail</div>
                </div>
                {expandedChamber && (
                  <button onClick={() => setExpandedChamber(null)} style={{
                    padding: '5px 14px', borderRadius: 6, border: `1px solid ${C.border}`,
                    background: '#fff', color: C.textMuted, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>Close Detail</button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
                {drillData.chambers.map((c, i) => {
                  const isExpanded = expandedChamber === c.chamber;
                  const barColor = c.utilPct > 90 ? C.danger : c.utilPct > 75 ? C.warning : C.primary;
                  const levels = drillData.chamberLevels[c.chamber] || [];
                  return (
                    <div key={i} style={{
                      borderRadius: 10, border: `1.5px solid ${isExpanded ? C.primary : C.border}`,
                      background: isExpanded ? `${C.primary}04` : '#fafbfc',
                      transition: 'all 0.3s ease', cursor: 'pointer',
                      gridColumn: isExpanded ? '1 / -1' : undefined,
                      boxShadow: isExpanded ? `0 4px 16px ${C.primary}15` : 'none',
                    }} onClick={() => setExpandedChamber(isExpanded ? null : c.chamber)}>
                      {/* Chamber summary header */}
                      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                        {/* Circular gauge */}
                        <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                          <svg viewBox="0 0 36 36" style={{ width: 48, height: 48, transform: 'rotate(-90deg)' }}>
                            <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e8ecf1" strokeWidth="3" />
                            <circle cx="18" cy="18" r="15.5" fill="none" stroke={barColor} strokeWidth="3"
                              strokeDasharray={`${Math.min(c.utilPct, 100)} 100`} strokeLinecap="round"
                              style={{ transition: 'stroke-dasharray 0.8s ease' }} />
                          </svg>
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: barColor }}>
                            {Math.round(c.utilPct)}%
                          </div>
                        </div>
                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{c.chamber || '-'}</span>
                            <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: `${barColor}12`, color: barColor, fontWeight: 600 }}>
                              {whName((c as any).wh || '')}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: 11 }}>
                            <span style={{ color: C.textMuted }}>Cap: <b style={{ color: C.text }}>{fmt(c.cap)}</b></span>
                            <span style={{ color: C.textMuted }}>Used: <b style={{ color: barColor }}>{fmt(c.used)}</b></span>
                            <span style={{ color: C.textMuted }}>Sellable: <b style={{ color: C.success }}>{fmt(c.sellable)}</b></span>
                            {c.blocked > 0 && <span style={{ color: C.danger }}>Blocked: <b>{fmt(c.blockedCap)}</b></span>}
                          </div>
                          {/* Stacked bar */}
                          <div style={{ height: 5, background: '#e8ecf1', borderRadius: 3, marginTop: 6, overflow: 'hidden', display: 'flex' }}>
                            <div style={{ height: '100%', width: `${c.cap > 0 ? (c.used / c.cap) * 100 : 0}%`, background: barColor, transition: 'width 0.8s ease' }} />
                            {c.blockedCap > 0 && <div style={{ height: '100%', width: `${(c.blockedCap / c.cap) * 100}%`, background: C.danger, transition: 'width 0.8s ease' }} />}
                            <div style={{ height: '100%', width: `${c.cap > 0 ? (c.sellable / c.cap) * 100 : 0}%`, background: C.success, transition: 'width 0.8s ease' }} />
                          </div>
                        </div>
                        {/* Expand arrow */}
                        <div style={{ fontSize: 14, color: C.textLight, transition: 'transform 0.3s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</div>
                      </div>
                      {/* Expanded: Level-by-level detail */}
                      {isExpanded && levels.length > 0 && (
                        <div style={{ padding: '0 16px 16px', animation: 'slideUp 0.3s ease' }} onClick={e => e.stopPropagation()}>
                          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Level-by-Level Detail — {levels.length} levels, {(c as any).bins || 0} bins
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                              {levels.map((lv, li) => {
                                const lvPct = lv.cap > 0 ? Math.round((lv.used / lv.cap) * 100) : 0;
                                const lvColor = lvPct > 90 ? C.danger : lvPct > 75 ? C.warning : C.primary;
                                const lvFree = Math.max(0, lv.cap - lv.used - lv.blockedCap);
                                const sizes = Object.entries(lv.shelfSizes).sort((a, b) => b[1] - a[1]);
                                return (
                                  <div key={li} style={{
                                    padding: '10px 12px', borderRadius: 8, background: '#fff',
                                    border: `1px solid ${lvPct > 90 ? `${C.danger}40` : C.border}`,
                                    transition: 'all 0.2s',
                                  }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                      <span style={{
                                        fontSize: 16, fontWeight: 800, color: lvColor,
                                        display: 'flex', alignItems: 'center', gap: 4,
                                      }}>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: C.textLight, textTransform: 'uppercase' }}>LVL</span>
                                        {lv.level}
                                      </span>
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                                        background: `${lvColor}12`, color: lvColor,
                                      }}>{lvPct}%</span>
                                    </div>
                                    {/* Mini bar */}
                                    <div style={{ height: 4, background: '#e8ecf1', borderRadius: 2, marginBottom: 6, overflow: 'hidden', display: 'flex' }}>
                                      <div style={{ height: '100%', width: `${Math.min(lvPct, 100)}%`, background: lvColor, borderRadius: 2 }} />
                                      {lv.blockedCap > 0 && <div style={{ height: '100%', width: `${(lv.blockedCap / lv.cap) * 100}%`, background: C.danger }} />}
                                    </div>
                                    <div style={{ fontSize: 10, color: C.textMuted, lineHeight: 1.6 }}>
                                      <div>Cap: <b style={{ color: C.text }}>{fmt(lv.cap)}</b> · Used: <b style={{ color: lvColor }}>{fmt(lv.used)}</b></div>
                                      <div>Free: <b style={{ color: C.success }}>{fmt(lvFree)}</b> · Bins: <b>{lv.bins}</b></div>
                                      {lv.blockedCap > 0 && <div style={{ color: C.danger }}>Blocked: <b>{fmt(lv.blockedCap)}</b></div>}
                                      {sizes.length > 0 && (
                                        <div style={{ marginTop: 3, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                          {sizes.slice(0, 3).map(([sz, cnt]) => (
                                            <span key={sz} style={{
                                              fontSize: 9, padding: '1px 5px', borderRadius: 3,
                                              background: '#f1f5f9', color: C.textMuted, fontWeight: 500,
                                            }}>{sz}: {cnt}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* Summary row */}
                            <div style={{
                              marginTop: 10, padding: '8px 12px', borderRadius: 6, background: `${C.primary}06`,
                              border: `1px solid ${C.primary}15`, display: 'flex', gap: 20, fontSize: 11,
                            }}>
                              <span style={{ color: C.textMuted }}>Total Cap: <b style={{ color: C.text }}>{fmt(c.cap)}</b></span>
                              <span style={{ color: C.textMuted }}>Total Used: <b style={{ color: barColor }}>{fmt(c.used)}</b></span>
                              <span style={{ color: C.textMuted }}>Sellable: <b style={{ color: C.success }}>{fmt(c.sellable)}</b></span>
                              <span style={{ color: C.textMuted }}>Levels: <b>{levels.length}</b></span>
                              {c.blocked > 0 && <span style={{ color: C.danger }}>Blocked Bins: <b>{c.blocked}</b></span>}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pallet Type + Chamber side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 }}>

              {/* Pallet Type Breakdown */}
              <div style={{ ...cardStyle, padding: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>Sellable by Pallet Type</div>
                <div style={{ maxHeight: 320, overflow: 'auto' }}>
                  {drillData.palletTypes.map((pt, i) => {
                    const usedPct = pt.cap > 0 ? (pt.used / pt.cap) * 100 : 0;
                    const blockedPct = pt.cap > 0 ? (pt.blockedCap / pt.cap) * 100 : 0;
                    const sellPct = pt.cap > 0 ? (pt.sellable / pt.cap) * 100 : 0;
                    return (
                      <div key={i} style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: '#fafbfc', border: `1px solid ${C.border}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{pt.type}</span>
                          <span style={{ fontSize: 11, color: C.textMuted }}>Cap: {fmt(pt.cap)}</span>
                        </div>
                        {/* Stacked bar */}
                        <div style={{ height: 8, background: '#e8ecf1', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
                          <div style={{ height: '100%', width: `${usedPct}%`, background: C.primary, transition: 'width 1s ease' }} />
                          <div style={{ height: '100%', width: `${blockedPct}%`, background: C.danger, transition: 'width 1s ease' }} />
                          <div style={{ height: '100%', width: `${sellPct}%`, background: C.success, transition: 'width 1s ease' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 5, fontSize: 10 }}>
                          <span style={{ color: C.primary }}>Used: <b>{fmt(pt.used)}</b></span>
                          {pt.blockedCap > 0 && <span style={{ color: C.danger }}>Blocked: <b>{fmt(pt.blockedCap)}</b></span>}
                          <span style={{ color: C.success, fontWeight: 700 }}>Sellable: <b>{fmt(pt.sellable)}</b></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Warehouse Detail Table */}
            <div style={{ ...cardStyle, padding: 20, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>Warehouse Detail</div>
              <div style={{ overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                      {['Warehouse', 'Rack Cap', 'Rack Used', 'Blocked', 'Virtual', 'Floor', 'Manual', 'Commit', 'Total Used', 'Sellable', 'Util %'].map(h => (
                        <th key={h} style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: C.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.warehouseRows.map((w, i) => {
                      // Use pre-computed values from stats (matches DCV)
                      const tUsed = w.tUsed;
                      const tFree = w.tFree;
                      const pct = w.tCap > 0 ? Math.min(100, Math.round((tUsed / w.tCap) * 1000) / 10) : 0;
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }} onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '8px 6px', fontWeight: 700, color: C.text, textAlign: 'left' }}>{whName(w.warehouse)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: C.textMuted }}>{fmt(w.rackCap)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: C.primary }}>{fmt(w.rackUsed)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: C.danger }}>{fmt(w.blockedCap)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: C.teal }}>{fmt(w.virtualUsed)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: C.success }}>{fmt(w.floorPallets)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: C.purple }}>{fmt(w.manualPallets)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', color: C.warning }}>{fmt(w.custShortfall)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 800, color: C.text }}>{fmt(tUsed)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 700, color: C.success }}>{fmt(tFree)}</td>
                          <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                              background: pct > 90 ? '#fef2f2' : pct > 75 ? '#fffbeb' : '#f0fdf4',
                              color: pct > 90 ? C.danger : pct > 75 ? C.warning : C.success,
                            }}>{pct}%</span>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row */}
                    <tr style={{ borderTop: `2px solid ${C.text}`, background: '#f8fafc' }}>
                      <td style={{ padding: '10px 6px', fontWeight: 800, color: C.text, textAlign: 'left' }}>TOTAL</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700 }}>{fmt(d.totalCapacity - (d.manualCap || 0))}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: C.primary }}>{fmt(d.rackUsed)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: C.danger }}>{fmt(d.blockedCap)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: C.teal }}>{fmt(d.virtualUsed)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: C.success }}>{fmt(d.floorPallets)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: C.purple }}>{fmt(d.manualPallets)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, color: C.warning }}>{fmt(d.custShortfall)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 800, color: C.text }}>{fmt(d.totalUsed)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 800, color: C.success }}>{fmt(d.totalFree)}</td>
                      <td style={{ padding: '10px 6px', textAlign: 'right' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700,
                          background: C.primary, color: '#fff',
                        }}>{utilPct}%</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Client Summary */}
            <div style={{ ...cardStyle, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>
                Top Clients ({drillData.clients.length})
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, maxHeight: 280, overflow: 'auto' }}>
                {drillData.clients.slice(0, 20).map((cl, i) => {
                  const pct = cl.cap > 0 ? Math.round((cl.used / cl.cap) * 100) : 0;
                  return (
                    <div key={i} style={{
                      padding: '12px 14px', borderRadius: 8, background: '#fafbfc',
                      border: `1px solid ${C.border}`, transition: 'all 0.2s',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cl.client}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
                        <span style={{ color: C.textMuted }}>Used: <b style={{ color: C.text }}>{fmt(cl.used)}</b></span>
                        {cl.cap > 0 && <span style={{ color: C.textLight }}>Cap: {fmt(cl.cap)}</span>}
                      </div>
                      {cl.cap > 0 && (
                        <div style={{ height: 4, background: '#e8ecf1', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: C.primary, borderRadius: 2, transition: 'width 0.8s ease' }} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── FOOTER ── */}
        <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 11, color: C.textLight }}>
          Hala Warehousing Intelligence Platform — Sales Operations Dashboard
        </div>
      </div>
    </div>
  );
};

export default SalesDashboard;
